import React from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const CaseStudyCard = ({ study, theme = 'default' }) => {
  const getThemeClasses = () => {
    switch (theme) {
      case 'events':
        return {
          card: 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200',
          accent: 'text-rose-600',
          button: 'bg-rose-600 hover:bg-rose-700 text-white'
        };
      case 'realestate':
        return {
          card: 'bg-gradient-to-br from-blue-50 to-slate-50 border-blue-200',
          accent: 'text-blue-600',
          button: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
      case 'marketing':
        return {
          card: 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200',
          accent: 'text-orange-600',
          button: 'bg-orange-600 hover:bg-orange-700 text-white'
        };
      case 'design':
        return {
          card: 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200',
          accent: 'text-emerald-600',
          button: 'bg-emerald-600 hover:bg-emerald-700 text-white'
        };
      default:
        return {
          card: 'bg-white border-gray-200',
          accent: 'text-primary',
          button: 'bg-primary hover:bg-primary/90 text-white'
        };
    }
  };

  const themeClasses = getThemeClasses();

  return (
    <div className={`group relative overflow-hidden rounded-xl border ${themeClasses?.card} hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
      {/* Image Container */}
      <div className="relative aspect-video overflow-hidden">
        <Image
          src={study?.image}
          alt={study?.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <Icon name="Play" size={24} color="white" />
          </div>
        </div>

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${themeClasses?.accent} bg-white/90 backdrop-blur-sm`}>
            {study?.category}
          </span>
        </div>

        {/* Duration Badge */}
        <div className="absolute top-4 right-4">
          <span className="px-2 py-1 rounded text-xs font-medium text-white bg-black/50 backdrop-blur-sm">
            {study?.duration}
          </span>
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <Icon name={study?.icon} size={16} className={themeClasses?.accent} />
          <span className={`text-sm font-medium ${themeClasses?.accent}`}>
            {study?.industry}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
          {study?.title}
        </h3>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {study?.description}
        </p>

        {/* Metrics */}
        <div className="flex items-center gap-4 mb-4">
          {study?.metrics?.map((metric, index) => (
            <div key={index} className="text-center">
              <div className={`text-lg font-bold ${themeClasses?.accent}`}>
                {metric?.value}
              </div>
              <div className="text-xs text-gray-500">
                {metric?.label}
              </div>
            </div>
          ))}
        </div>

        {/* Client Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image
              src={study?.client?.logo}
              alt={study?.client?.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div>
              <div className="text-sm font-medium text-gray-900">
                {study?.client?.name}
              </div>
              <div className="text-xs text-gray-500">
                {study?.client?.location}
              </div>
            </div>
          </div>

          <Link
            to={`/case-studies/${study?.slug}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${themeClasses?.button} transition-colors`}
          >
            View Study
            <Icon name="ArrowRight" size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyCard;